import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ResetPasswordForm } from "@/components/ResetPasswordForm";
import { apiRequest } from "@/lib/queryClient";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [location] = useLocation();

  // Robust token parsing: prefer actual browser location, fallback to wouter location
  const browserQS = typeof window !== "undefined" ? window.location.search : "";
  const queryString = browserQS || (location.includes("?") ? location.split("?")[1] : "");
  const urlParams = new URLSearchParams(
    queryString.startsWith("?") ? queryString.slice(1) : queryString
  );
  const token = urlParams.get("token");

  // Remove debug logs for production

  // Validate token with server
  const {
    data: tokenValidation,
    isLoading: isValidating,
    error,
  } = useQuery({
    queryKey: ["validate-reset-token", token],
    queryFn: async () => {
      if (!token) return { valid: false, message: "Brak tokenu" };
      console.log("🔍 Validating token:", token);
      try {
        const json = await apiRequest(
          "GET",
          `/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`
        );
        console.log("✅ Token validation response:", json);
        return json;
      } catch (error) {
        console.error("❌ Token validation error:", error);
        throw error;
      }
    },
    enabled: !!token,
    retry: false,
  });

  // Debug logging
  console.log("🔍 Reset password page state:", {
    token: token ? `${token.substring(0, 8)}...` : null,
    tokenValidation,
    isValidating,
    error: error?.message,
  });

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#030407] via-red-950 to-[#030407] p-4">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white text-lg">{t("resetPassword.validatingToken")}</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#030407] via-red-950 to-[#030407] p-4">
        <div className="w-full max-w-md">
          <div className="text-center text-white">
            <h1 className="text-heading-2 font-extrabold mb-6">{t("resetPassword.invalidLink")}</h1>
            <p className="text-body font-body text-white/70 mb-8">
              {t("resetPassword.invalidLinkDesc")}
            </p>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              data-testid="link-back-to-login"
            >
              {t("resetPassword.backToLogin")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenValidation && !tokenValidation.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#030407] via-red-950 to-[#030407] p-4">
        <div className="bg-[#0a0c10]/80 backdrop-blur-sm border border-white/[0.08] rounded-lg p-8 max-w-md w-full text-center">
          <h1 className="text-heading-2 font-extrabold text-white mb-6">
            {t("resetPassword.invalidLink")}
          </h1>
          <p className="text-body font-body text-white/70 mb-8">
            {tokenValidation.message || t("resetPassword.invalidLinkDesc")}
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            data-testid="button-back-to-login"
          >
            {t("resetPassword.backToLogin")}
          </button>
        </div>
      </div>
    );
  }

  // Show form if token is valid
  if (tokenValidation && tokenValidation.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#030407] via-red-950 to-[#030407] p-4">
        <ResetPasswordForm token={token} />
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#030407] via-red-950 to-[#030407] p-4">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-white text-lg">{t("resetPassword.loading")}</p>
      </div>
    </div>
  );
}
